import {
  Box,
  FormControl,
  InputLabel,
  OutlinedInput,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import type { RelatorioPaymentsTipoData } from "interfaces/reports";
import { EXTRATO_ACCENT, extratoToolbarSx } from "./extrato.theme";

export type PeriodPreset = "day" | "week" | "month" | "custom";

type Props = {
  periodPreset: PeriodPreset;
  periodoLabel: string;
  dataInicio: string;
  dataFim: string;
  tipoData: RelatorioPaymentsTipoData;
  loading?: boolean;
  onPeriodPresetChange: (preset: "day" | "week" | "month") => void;
  onDataInicioChange: (value: string) => void;
  onDataFimChange: (value: string) => void;
  onAplicarPeriodo: () => void;
  onTipoDataChange: (tipo: RelatorioPaymentsTipoData) => void;
};

export default function ExtratoFiltros({
  periodPreset,
  periodoLabel,
  dataInicio,
  dataFim,
  tipoData,
  loading,
  onPeriodPresetChange,
  onDataInicioChange,
  onDataFimChange,
  onAplicarPeriodo,
  onTipoDataChange,
}: Props) {
  return (
    <MDBox sx={extratoToolbarSx}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap">
          <AccountBalanceWalletOutlinedIcon sx={{ color: EXTRATO_ACCENT, mt: 0.25 }} />
          <Box minWidth={0}>
            <MDTypography variant="button" fontWeight="medium" color="text">
              Recebimentos de inscrições
            </MDTypography>
            <Typography variant="caption" color="text.secondary" display="block">
              {periodoLabel}
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", lg: "center" }}
          flexWrap="wrap"
        >
          <ToggleButtonGroup
            exclusive
            size="small"
            value={periodPreset === "custom" ? "" : periodPreset}
            onChange={(_, next: "day" | "week" | "month" | null) => {
              if (next) onPeriodPresetChange(next);
            }}
            sx={{
              flexShrink: 0,
              "& .MuiToggleButton-root": {
                px: 1.5,
                py: 0.75,
                textTransform: "none",
                fontSize: "0.8125rem",
                "&.Mui-selected": {
                  bgcolor: "rgba(249, 96, 48, 0.1)",
                  borderColor: EXTRATO_ACCENT,
                  color: EXTRATO_ACCENT,
                  fontWeight: 600,
                },
              },
            }}
          >
            <ToggleButton value="day">Hoje</ToggleButton>
            <ToggleButton value="week">Semana</ToggleButton>
            <ToggleButton value="month">Mês</ToggleButton>
          </ToggleButtonGroup>

          <FormControl variant="outlined" size="small" sx={{ minWidth: 160, flex: 1 }}>
            <InputLabel shrink htmlFor="extrato-data-inicio">
              Data inicial
            </InputLabel>
            <OutlinedInput
              id="extrato-data-inicio"
              type="date"
              value={dataInicio}
              onChange={(e) => onDataInicioChange(e.target.value)}
              label="Data inicial"
              notched
              sx={{ bgcolor: "background.paper", borderRadius: 1.5 }}
            />
          </FormControl>

          <FormControl variant="outlined" size="small" sx={{ minWidth: 160, flex: 1 }}>
            <InputLabel shrink htmlFor="extrato-data-fim">
              Data final
            </InputLabel>
            <OutlinedInput
              id="extrato-data-fim"
              type="date"
              value={dataFim}
              onChange={(e) => onDataFimChange(e.target.value)}
              label="Data final"
              notched
              sx={{ bgcolor: "background.paper", borderRadius: 1.5 }}
            />
          </FormControl>

          <MDButton
            variant="outlined"
            color="dark"
            size="small"
            disabled={loading}
            onClick={onAplicarPeriodo}
            sx={{ alignSelf: { xs: "flex-start", lg: "center" } }}
          >
            Aplicar período
          </MDButton>
        </Stack>

        <Stack spacing={0.75}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Critério de data
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={tipoData}
            onChange={(_, next: RelatorioPaymentsTipoData | null) => {
              if (next) onTipoDataChange(next);
            }}
            sx={{
              flexWrap: "wrap",
              "& .MuiToggleButton-root": {
                px: 1.25,
                py: 0.75,
                textTransform: "none",
                fontSize: "0.8125rem",
                "&.Mui-selected": {
                  bgcolor: "rgba(249, 96, 48, 0.1)",
                  borderColor: EXTRATO_ACCENT,
                  color: EXTRATO_ACCENT,
                  fontWeight: 600,
                },
              },
            }}
          >
            <ToggleButton value="pagamento">Data de pagamento</ToggleButton>
            <ToggleButton value="inscricao">Data de inscrição</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary">
            Cortesias e gratuitas não entram nos totais.
          </Typography>
        </Stack>
      </Stack>
    </MDBox>
  );
}
